import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, type AppRole } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import {
  createTeamMember,
  updateTeamMemberRole,
  upsertPayRate,
  setMemberActive,
  resetMemberPassword,
  updateMemberPhone,
  updateMemberNotes,
  updateMemberName,
  updateMemberEmail,
} from "@/utils/team.functions";
import { ensureClockInToken } from "@/utils/clock-token.functions";
import { grantCompanyAccess } from "@/utils/company-access.functions";
import { buildWhatsAppUrl, validateAndNormalizePhone } from "@/lib/phone";
import { buildClockUrl } from "@/lib/clock-url";
import { copyTextToClipboard, cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  MoreVertical,
  KeyRound,
  Trash2,
  Users,
  Copy,
  Check,
  MessageCircle,
  Link2,
  RefreshCw,
  Phone,
  QrCode,
  Crown,
  Shield,
  HardHat,
  Calculator,
  User,
  UserCheck,
  Pencil,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Building2,
  type LucideIcon,
} from "lucide-react";
import { ClockTokenQrDialog } from "@/components/team/ClockTokenQrDialog";
import { WorkerIntakeCard } from "@/components/team/WorkerIntakeCard";
import {
  deriveIntakeStatus,
  applyW9Status,
  INTAKE_STATUS_ORDER,
  type IntakeStatusKind,
} from "@/lib/worker-intake-status";
import type { W9Like } from "@/lib/w9-completeness";
import { IntakeStatusBadge } from "@/components/team/IntakeStatusBadge";
import { useScopedCompanyId } from "@/owner/useScopedCompanyId";
import { useOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";

export const Route = createFileRoute("/_app/team")({
  component: TeamPage,
});

const ROLE_VALUES: AppRole[] = ["admin", "supervisor", "employee", "estimate", "client"];

function useRoleLabel() {
  const { t } = useTranslation();
  return (r: AppRole | null) =>
    r ? t(`teamPage.role.labels.${r}`, { defaultValue: r as string }) : "";
}

type PayType = "hourly" | "daily";

type Member = {
  id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  role: AppRole | null;
  hourly_rate: number;
  pay_type: PayType;
  company_id: string;
  /** True when this person has a role in more than one company (a "gestor"). */
  isGestor: boolean;
  /** True when the worker receives a 1099 (enables the W-9 intake section). */
  is1099: boolean;
  /** Status do cadastro do trabalhador (worker-intake). `null` = link nunca gerado. */
  intakeStatus: IntakeStatusKind | null;
};

type RoleMeta = { icon: LucideIcon; badge: string; avatar: string };

const ROLE_META: Record<string, RoleMeta> = {
  admin: {
    icon: Crown,
    badge:
      "border-violet-300 bg-violet-100 text-violet-700 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-300",
    avatar: "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200",
  },
  supervisor: {
    icon: Shield,
    badge:
      "border-blue-300 bg-blue-100 text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300",
    avatar: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200",
  },
  employee: {
    icon: HardHat,
    badge:
      "border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
    avatar: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200",
  },
  estimate: {
    icon: Calculator,
    badge:
      "border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
    avatar: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200",
  },
  client: {
    icon: User,
    badge:
      "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300",
    avatar: "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300",
  },
};

const GESTOR_META: RoleMeta = {
  icon: Building2,
  badge:
    "border-fuchsia-300 bg-fuchsia-100 text-fuchsia-700 dark:border-fuchsia-800 dark:bg-fuchsia-950/50 dark:text-fuchsia-300",
  avatar: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/50 dark:text-fuchsia-200",
};

const FALLBACK_ROLE_META: RoleMeta = {
  icon: User,
  badge:
    "border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300",
  avatar: "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300",
};

function roleMeta(role: AppRole | null): RoleMeta {
  return (role && ROLE_META[role]) || FALLBACK_ROLE_META;
}

function memberInitials(m: { full_name: string | null; email: string }): string {
  const name = (m.full_name ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return (m.email?.[0] ?? "?").toUpperCase();
}

type SortKey = "name" | "contact" | "role" | "rate" | "intake" | "active";
type SortState = { key: SortKey; dir: "asc" | "desc" };

function SortHead({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "-ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 hover:text-foreground",
          active ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
        <Icon className={cn("h-3.5 w-3.5", active ? "opacity-100" : "opacity-50")} />
      </button>
    </TableHead>
  );
}

function TeamPage() {
  const { t } = useTranslation();
  const roleLabel = useRoleLabel();
  const { user, company, hasRole, companies: authCompanies, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole("admin");
  const scopedCompanyId = useScopedCompanyId();
  const {
    isOwner,
    companies: ownerCompanies,
    selectedCompanyId,
    setSelectedCompanyId,
    getCompany,
    loading: ownerLoading,
  } = useOwnerCompany();

  // Companies the current user can grant admin access into: all of them for the
  // platform owner, otherwise the companies the user (a multi-company admin) belongs to.
  const grantableCompanies: { id: string; name: string }[] = isOwner
    ? ownerCompanies.map((c) => ({ id: c.id, name: c.name }))
    : authCompanies;
  const selectedCompany =
    isOwner && selectedCompanyId !== "all" ? getCompany(selectedCompanyId) : undefined;

  const createFn = useServerFn(createTeamMember);
  const updateRoleFn = useServerFn(updateTeamMemberRole);
  const upsertRateFn = useServerFn(upsertPayRate);
  const setActiveFn = useServerFn(setMemberActive);
  const resetPwdFn = useServerFn(resetMemberPassword);
  const updatePhoneFn = useServerFn(updateMemberPhone);
  const updateNotesFn = useServerFn(updateMemberNotes);
  const updateNameFn = useServerFn(updateMemberName);
  const updateEmailFn = useServerFn(updateMemberEmail);
  const ensureTokenFn = useServerFn(ensureClockInToken);
  const grantFn = useServerFn(grantCompanyAccess);

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState<SortState>({ key: "name", dir: "asc" });
  const toggleSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );

  const [addOpen, setAddOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState<Member | null>(null);
  const [qrOpen, setQrOpen] = useState<Member | null>(null);
  const [showPwd, setShowPwd] = useState<{ email: string; password: string } | null>(null);
  const [deleteMember, setDeleteMember] = useState<Member | null>(null);
  const [editMember, setEditMember] = useState<Member | null>(null);
  const [grantMember, setGrantMember] = useState<Member | null>(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      toast.error(t("teamPage.onlyAdmins"));
      navigate({ to: "/dashboard" });
    }
  }, [authLoading, isAdmin, navigate, t]);

  const load = async () => {
    if (!isOwner && !company?.id) return;
    setLoading(true);
    let profilesQ = supabase
      .from("profiles")
      .select("id, full_name, email, phone, notes, is_active, created_at, company_id")
      .order("created_at", { ascending: true });
    let rolesQ = supabase.from("user_roles").select("user_id, role, company_id");
    let ratesQ = supabase
      .from("employee_pay_rates")
      .select("user_id, hourly_rate, pay_type, created_at, company_id")
      .order("created_at", { ascending: false });
    // TODO(types): remover cast após gen types (coluna is_1099_contractor nova em profiles)
    let flagsQ = (supabase as any).from("profiles").select("id, is_1099_contractor");
    if (scopedCompanyId) {
      profilesQ = profilesQ.eq("company_id", scopedCompanyId);
      rolesQ = rolesQ.eq("company_id", scopedCompanyId);
      ratesQ = ratesQ.eq("company_id", scopedCompanyId);
      flagsQ = flagsQ.eq("company_id", scopedCompanyId);
    }
    const [{ data: profiles }, { data: roles }, { data: rates }, { data: flags }] =
      await Promise.all([profilesQ, rolesQ, ratesQ, flagsQ]);

    const flag1099Map = new Map<string, boolean>();
    ((flags ?? []) as Array<{ id: string; is_1099_contractor: boolean | null }>).forEach((f) =>
      flag1099Map.set(f.id, Boolean(f.is_1099_contractor)),
    );

    const roleMap = new Map<string, AppRole>();
    (roles ?? []).forEach((r) => roleMap.set(r.user_id, r.role as AppRole));

    // Detect "gestor": a member with a role in more than one company. Uses an
    // UNSCOPED user_roles read (owner sees all companies via RLS; a regular admin
    // only sees their own, so gestor detection is naturally owner/self-visible).
    const memberIds = (profiles ?? []).map((p) => p.id);
    const companySets = new Map<string, Set<string>>();
    if (memberIds.length) {
      const { data: allUserRoles } = await supabase
        .from("user_roles")
        .select("user_id, company_id")
        .in("user_id", memberIds);
      (allUserRoles ?? []).forEach((r) => {
        const set = companySets.get(r.user_id) ?? new Set<string>();
        set.add(r.company_id);
        companySets.set(r.user_id, set);
      });
    }

    // Status do cadastro (worker-intake) de todos os membros numa única query.
    // A RLS ("admins read intake tokens") já escopa por empresa; o owner enxerga tudo.
    // TODO(types): remover cast após gen types (tabela worker_intake_tokens nova)
    const intakeMap = new Map<string, IntakeStatusKind | null>();
    if (memberIds.length) {
      const { data: tokens } = await (supabase as any)
        .from("worker_intake_tokens")
        .select("user_id, revoked_at, submitted_at, last_opened_at")
        .in("user_id", memberIds);
      (
        (tokens ?? []) as Array<{
          user_id: string;
          revoked_at: string | null;
          submitted_at: string | null;
          last_opened_at: string | null;
        }>
      ).forEach((tk) => intakeMap.set(tk.user_id, deriveIntakeStatus(tk)));
    }

    // W-9 dos membros (colunas explícitas; NUNCA tin_encrypted, que é REVOKE p/ authenticated).
    // RLS: policy "admins read tax" (admin da empresa) + "owner all tax".
    // TODO(types): remover cast após gen types (tabela contractor_tax_info nova)
    const w9Map = new Map<string, W9Like>();
    if (memberIds.length) {
      const { data: taxRows } = await (supabase as any)
        .from("contractor_tax_info")
        .select("user_id, legal_name, tin_last4, address_line1, city, state, postal_code")
        .in("user_id", memberIds);
      ((taxRows ?? []) as Array<W9Like & { user_id: string }>).forEach((row) =>
        w9Map.set(row.user_id, row),
      );
    }

    const rateMap = new Map<string, { rate: number; pay_type: PayType }>();
    (rates ?? []).forEach((r) => {
      if (!rateMap.has(r.user_id)) {
        rateMap.set(r.user_id, {
          rate: Number(r.hourly_rate),
          pay_type: (r.pay_type as PayType) ?? "hourly",
        });
      }
    });

    setMembers(
      (profiles ?? []).map((p) => {
        const rate = rateMap.get(p.id);
        return {
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          phone: p.phone,
          notes: p.notes,
          is_active: p.is_active,
          created_at: p.created_at,
          company_id: p.company_id,
          role: roleMap.get(p.id) ?? null,
          hourly_rate: rate?.rate ?? 0,
          pay_type: rate?.pay_type ?? "hourly",
          isGestor: (companySets.get(p.id)?.size ?? 0) > 1,
          is1099: flag1099Map.get(p.id) ?? false,
          intakeStatus: applyW9Status(intakeMap.get(p.id) ?? null, {
            is1099: flag1099Map.get(p.id) ?? false,
            w9: w9Map.get(p.id) ?? null,
          }),
        };
      }),
    );
    setLoading(false);
  };

  useEffect(() => {
    // For owner, wait until the OwnerCompanyContext finished loading so that
    // scopedCompanyId reflects the up-to-date selection (and isn't stuck on a
    // stale value from localStorage pointing to a removed company).
    if (isOwner && ownerLoading) return;
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, scopedCompanyId, isOwner, ownerLoading]);

  const filtered = useMemo(() => {
    const list = members
      .filter((m) => (showInactive ? true : m.is_active))
      .filter((m) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (m.full_name ?? "").toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
      });
    const nameOf = (m: Member) => (m.full_name?.trim() || m.email || "").toLowerCase();
    const roleRank = (m: Member) => (m.role ? ROLE_VALUES.indexOf(m.role) : ROLE_VALUES.length);
    return [...list].sort((a, b) => {
      let r = 0;
      switch (sort.key) {
        case "rate":
          r = a.hourly_rate - b.hourly_rate;
          break;
        case "intake":
          r =
            INTAKE_STATUS_ORDER[a.intakeStatus ?? "noLink"] -
            INTAKE_STATUS_ORDER[b.intakeStatus ?? "noLink"];
          break;
        case "active":
          r = (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0);
          break;
        case "role":
          r = roleRank(a) - roleRank(b);
          break;
        case "contact":
          r = (a.phone ?? "").localeCompare(b.phone ?? "");
          break;
        default:
          r = nameOf(a).localeCompare(nameOf(b));
      }
      // Stable tiebreak by name so equal keys keep a predictable order.
      if (r === 0 && sort.key !== "name") r = nameOf(a).localeCompare(nameOf(b));
      return sort.dir === "asc" ? r : -r;
    });
  }, [members, showInactive, search, sort]);

  const stats = useMemo(() => {
    const active = members.filter((m) => m.is_active);
    return {
      total: members.length,
      active: active.length,
      employees: active.filter((m) => m.role === "employee" || m.role === "supervisor").length,
    };
  }, [members]);

  const handleActiveToggle = async (m: Member, isActive: boolean) => {
    try {
      await setActiveFn({ data: { userId: m.id, isActive } });
      toast.success(isActive ? t("teamPage.active.activated") : t("teamPage.active.deactivated"));
      load();
    } catch (e) {
      toast.error(t("teamPage.active.updateFailed"), {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  const getClockLink = async (m: Member, rotate = false) => {
    const { token } = await ensureTokenFn({ data: { userId: m.id, rotate } });
    return buildClockUrl(token);
  };

  const buildWhatsAppTextOnlyUrl = (message: string) => {
    const isMobile =
      typeof navigator !== "undefined" &&
      /android|iphone|ipad|ipod|mobile|windows phone/i.test(navigator.userAgent);

    return isMobile
      ? `https://wa.me/?text=${encodeURIComponent(message)}`
      : `https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  };

  const handleShareWhatsApp = async (m: Member, prebuiltUrl?: string) => {
    // Resolve the WhatsApp URL FIRST, then open the tab. Opening
    // `about:blank` synchronously and redirecting later leaves a blank
    // tab if anything in the async chain fails or is slow on mobile.
    let waUrl: string;
    try {
      const url = prebuiltUrl ?? (await getClockLink(m, false));
      const msg = t("teamPage.whatsapp.message", {
        name: m.full_name ?? "",
        url,
      });

      const rawPhone = m.phone ?? "";
      if (rawPhone.trim()) {
        const wa = buildWhatsAppUrl(rawPhone, msg);
        if ("error" in wa) {
          toast.error(t("teamPage.phone.invalid"), { description: wa.error });
          return;
        }
        waUrl = wa.url;
      } else {
        waUrl = buildWhatsAppTextOnlyUrl(msg);
      }
    } catch (e) {
      toast.error(t("teamPage.whatsapp.openFailed"), {
        description: e instanceof Error ? e.message : undefined,
      });
      return;
    }

    const opened = window.open(waUrl, "_blank", "noopener,noreferrer");
    if (!opened) {
      try {
        await copyTextToClipboard(waUrl);
        toast.success(t("teamPage.whatsapp.linkCopied"), {
          description: t("teamPage.whatsapp.blockedDesc"),
        });
      } catch {
        toast.error(t("teamPage.whatsapp.blocked"));
      }
    }
  };

  const handleOpenWhatsAppChat = (m: Member) => {
    const rawPhone = m.phone ?? "";
    if (!rawPhone.trim()) {
      toast.error(t("teamPage.phone.addToUseWhatsapp"));
      return;
    }

    const result = validateAndNormalizePhone(rawPhone);
    if (!result.ok) {
      toast.error(t("teamPage.phone.invalid"), { description: result.error });
      return;
    }

    const appUrl = `whatsapp://send?phone=${result.waNumber}`;
    const webUrl = `https://web.whatsapp.com/send?phone=${result.waNumber}`;
    const isMobile =
      typeof navigator !== "undefined" &&
      /android|iphone|ipad|ipod|mobile|windows phone/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = appUrl;
      window.setTimeout(() => {
        window.open(webUrl, "_blank", "noopener,noreferrer");
      }, 900);
      return;
    }

    const opened = window.open(webUrl, "_blank", "noopener,noreferrer");
    if (!opened) {
      copyTextToClipboard(webUrl).then(
        () =>
          toast.success(t("teamPage.whatsapp.linkCopied"), {
            description: t("teamPage.whatsapp.blockedDesc"),
          }),
        () => toast.error(t("teamPage.whatsapp.blocked")),
      );
    }
  };

  const handleCopyLink = async (m: Member, rotate = false) => {
    try {
      const url = await getClockLink(m, rotate);
      await copyTextToClipboard(url);
      toast.success(t("teamPage.link.copied"));
    } catch (e) {
      toast.error(t("teamPage.link.copyFailed"), {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  const handleRotateLink = async (m: Member) => {
    try {
      await getClockLink(m, true);
      setQrOpen(m);
      toast.success(t("teamPage.link.newGenerated"));
    } catch (e) {
      toast.error(t("teamPage.link.newFailed"), {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  if (authLoading || !isAdmin) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("teamPage.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("teamPage.subtitle")}</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              {t("teamPage.addMember")}
            </Button>
          </DialogTrigger>
          <AddMemberDialog
            onClose={() => setAddOpen(false)}
            onCreated={(res) => {
              setAddOpen(false);
              load();
              if (res.tempPassword) {
                setShowPwd({ email: res.email, password: res.tempPassword });
              } else {
                toast.success(t("teamPage.memberAdded"));
              }
            }}
            createFn={createFn}
          />
        </Dialog>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          label={t("teamPage.stats.total")}
          value={stats.total}
          icon={Users}
          accent="bg-blue-100 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300"
        />
        <StatCard
          label={t("teamPage.stats.active")}
          value={stats.active}
          icon={UserCheck}
          accent="bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300"
        />
        <StatCard
          label={t("teamPage.stats.fieldWorkers")}
          value={stats.employees}
          icon={HardHat}
          accent="bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-300"
        />
      </div>

      {isOwner && selectedCompany && (
        <div className="flex flex-col gap-2 rounded-lg border border-amber-300/50 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Mostrando apenas membros de <strong>{selectedCompany.name}</strong>.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start sm:self-auto"
            onClick={() => setSelectedCompanyId("all")}
          >
            Ver todas as empresas
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
              <Users className="h-5 w-5" />
            </span>
            <div>
              <CardTitle className="text-base">{t("teamPage.table.members")}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {t("teamPage.table.membersCount", {
                  defaultValue: "{{total}} no total · {{active}} ativos",
                  total: stats.total,
                  active: stats.active,
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Switch checked={showInactive} onCheckedChange={setShowInactive} id="show-inactive" />
              <Label htmlFor="show-inactive" className="cursor-pointer">
                {t("teamPage.table.showInactive")}
              </Label>
            </div>
            <Input
              placeholder={t("teamPage.table.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-64"
            />
          </div>
        </CardHeader>
        <CardContent className="px-0">
          {loading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-muted-foreground">
              {t("teamPage.table.noMembers")}
            </div>
          ) : (
            <div className="max-h-[34rem] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <SortHead
                      label={t("teamPage.table.member", { defaultValue: "Membro" })}
                      sortKey="name"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortHead
                      label={t("teamPage.table.contact", { defaultValue: "Contato" })}
                      sortKey="contact"
                      sort={sort}
                      onSort={toggleSort}
                      className="hidden md:table-cell"
                    />
                    <SortHead
                      label={t("teamPage.table.role")}
                      sortKey="role"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortHead
                      label={t("teamPage.table.hourlyRate")}
                      sortKey="rate"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortHead
                      label={t("teamPage.table.intake", { defaultValue: "Cadastro" })}
                      sortKey="intake"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortHead
                      label={t("teamPage.table.active")}
                      sortKey="active"
                      sort={sort}
                      onSort={toggleSort}
                      className="text-center"
                    />
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((m) => (
                    <MemberRow
                      key={m.id}
                      member={m}
                      isMe={m.id === user?.id}
                      currency={company?.currency ?? "USD"}
                      onEdit={() => setEditMember(m)}
                      onActiveToggle={handleActiveToggle}
                      onResetPassword={() => setResetOpen(m)}
                      onShowQr={() => setQrOpen(m)}
                      onShareWhatsApp={() => handleShareWhatsApp(m)}
                      onOpenWhatsAppChat={() => handleOpenWhatsAppChat(m)}
                      onCopyLink={() => handleCopyLink(m, false)}
                      onRotateLink={() => handleRotateLink(m)}
                      onDelete={() => setDeleteMember(m)}
                      onGrantCompany={() => setGrantMember(m)}
                      canGrantCompany={
                        grantableCompanies.filter((c) => c.id !== m.company_id).length > 0
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!resetOpen} onOpenChange={(o) => !o && setResetOpen(null)}>
        {resetOpen && (
          <ResetPasswordDialog
            member={resetOpen}
            onClose={() => setResetOpen(null)}
            onDone={() => setResetOpen(null)}
            resetFn={resetPwdFn}
          />
        )}
      </Dialog>

      <Dialog open={!!showPwd} onOpenChange={(o) => !o && setShowPwd(null)}>
        {showPwd && (
          <ShowPasswordDialog
            email={showPwd.email}
            password={showPwd.password}
            onClose={() => setShowPwd(null)}
          />
        )}
      </Dialog>

      {qrOpen && (
        <ClockTokenQrDialog
          open={!!qrOpen}
          onOpenChange={(o) => !o && setQrOpen(null)}
          memberName={qrOpen.full_name ?? qrOpen.email}
          ensureToken={(rotate) => ensureTokenFn({ data: { userId: qrOpen.id, rotate } })}
          onShareWhatsApp={(url) => handleShareWhatsApp(qrOpen, url)}
        />
      )}

      <Dialog open={!!deleteMember} onOpenChange={(o) => !o && setDeleteMember(null)}>
        {deleteMember && (
          <DeleteMemberDialog
            member={deleteMember}
            onClose={() => setDeleteMember(null)}
            onDeleted={() => {
              setDeleteMember(null);
              load();
            }}
          />
        )}
      </Dialog>

      <Dialog open={!!grantMember} onOpenChange={(o) => !o && setGrantMember(null)}>
        {grantMember && (
          <GrantCompanyDialog
            member={grantMember}
            companies={grantableCompanies.filter((c) => c.id !== grantMember.company_id)}
            onClose={() => setGrantMember(null)}
            grantFn={grantFn}
          />
        )}
      </Dialog>

      <MemberEditSheet
        member={editMember}
        isMe={editMember?.id === user?.id}
        currency={company?.currency ?? "USD"}
        onClose={() => setEditMember(null)}
        onSaved={() => {
          setEditMember(null);
          load();
        }}
        fns={{
          updateName: (userId, fullName) => updateNameFn({ data: { userId, fullName } }),
          updateEmail: (userId, email) => updateEmailFn({ data: { userId, email } }),
          updatePhone: (userId, phone) => updatePhoneFn({ data: { userId, phone } }),
          updateRole: (userId, role) => updateRoleFn({ data: { userId, role } }),
          upsertRate: (userId, hourlyRate, payType) =>
            upsertRateFn({ data: { userId, hourlyRate, payType } }),
          updateNotes: (userId, notes) => updateNotesFn({ data: { userId, notes } }),
        }}
      />
    </div>
  );
}

function DeleteMemberDialog({
  member,
  onClose,
  onDeleted,
}: {
  member: Member;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const expectedName = member.full_name?.trim() || member.email;
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const canDelete = confirmText.trim().toLowerCase() === expectedName.toLowerCase() && !deleting;

  const handleDelete = async () => {
    setDeleting(true);
    // delete_company_employee ainda não está nos tipos gerados (migration nova).
    const { error } = await (
      supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ error: { message: string } | null }>
    )("delete_company_employee", { _user_id: member.id });
    setDeleting(false);
    if (error) {
      toast.error(t("teamPage.delete.failed", { defaultValue: "Falha ao excluir funcionário" }), {
        description: error.message,
      });
      return;
    }
    toast.success(t("teamPage.delete.done", { defaultValue: "Funcionário excluído" }));
    onDeleted();
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="text-destructive">
          {t("teamPage.delete.title", { defaultValue: "Excluir funcionário" })}
        </DialogTitle>
        <DialogDescription className="space-y-2 pt-1">
          <span className="block">
            {t("teamPage.delete.warning", {
              defaultValue:
                "Isto remove {{name}} e TODOS os dados pessoais dele (ponto, selfies, taxas, login). Esta ação NÃO pode ser desfeita.",
              name: expectedName,
            })}
          </span>
          <span className="block text-xs">
            {t("teamPage.delete.keepsPaid", {
              defaultValue:
                "As folhas de pagamento JÁ PAGAS são preservadas (com o nome registrado).",
            })}
          </span>
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="confirm-delete">
          {t("teamPage.delete.typeName", {
            defaultValue: 'Para confirmar, digite o nome: "{{name}}"',
            name: expectedName,
          })}
        </Label>
        <Input
          id="confirm-delete"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={expectedName}
          autoComplete="off"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={deleting}>
          {t("common.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button variant="destructive" onClick={handleDelete} disabled={!canDelete}>
          {deleting
            ? t("teamPage.delete.deleting", { defaultValue: "Excluindo…" })
            : t("teamPage.delete.confirm", { defaultValue: "Excluir definitivamente" })}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number;
  icon: LucideIcon;
  accent: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 pt-6">
        <span
          className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", accent)}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold leading-tight">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function GrantCompanyDialog({
  member,
  companies,
  onClose,
  grantFn,
}: {
  member: Member;
  companies: { id: string; name: string }[];
  onClose: () => void;
  grantFn: ReturnType<typeof useServerFn<typeof grantCompanyAccess>>;
}) {
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const memberName = member.full_name?.trim() || member.email;

  const handleGrant = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      const res = (await grantFn({ data: { userId: member.id, companyId } })) as {
        granted: boolean;
        alreadyHad: boolean;
      };
      toast.success(
        res.alreadyHad
          ? t("teamPage.grantCompany.already", { defaultValue: "Já tinha acesso a essa empresa." })
          : t("teamPage.grantCompany.done", { defaultValue: "Acesso concedido." }),
      );
      onClose();
    } catch (e) {
      toast.error(t("teamPage.grantCompany.failed", { defaultValue: "Falha ao conceder acesso" }), {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          {t("teamPage.grantCompany.title", { defaultValue: "Dar acesso a outra empresa" })}
        </DialogTitle>
        <DialogDescription>
          {t("teamPage.grantCompany.desc", {
            defaultValue:
              "{{name}} passa a ser admin da empresa escolhida e poderá alternar entre as empresas no seletor do topo.",
            name: memberName,
          })}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2 py-1">
        <Label>{t("companySwitcher.label", { defaultValue: "Empresa" })}</Label>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger>
            <SelectValue
              placeholder={t("teamPage.grantCompany.select", {
                defaultValue: "Selecione a empresa",
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          {t("common.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button onClick={handleGrant} disabled={!companyId || saving}>
          {saving
            ? t("common.saving", { defaultValue: "Salvando..." })
            : t("teamPage.grantCompany.confirm", { defaultValue: "Conceder acesso" })}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function MemberRow({
  member,
  isMe,
  currency,
  onEdit,
  onActiveToggle,
  onResetPassword,
  onShowQr,
  onShareWhatsApp,
  onOpenWhatsAppChat,
  onCopyLink,
  onRotateLink,
  onDelete,
  onGrantCompany,
  canGrantCompany,
}: {
  member: Member;
  isMe: boolean;
  currency: string;
  onEdit: () => void;
  onActiveToggle: (m: Member, a: boolean) => void;
  onResetPassword: () => void;
  onShowQr: () => void;
  onShareWhatsApp: () => void;
  onOpenWhatsAppChat: () => void;
  onCopyLink: () => void;
  onRotateLink: () => void;
  onDelete: () => void;
  onGrantCompany: () => void;
  canGrantCompany: boolean;
}) {
  const { t } = useTranslation();
  const roleLabel = useRoleLabel();
  const meta = member.isGestor ? GESTOR_META : roleMeta(member.role);
  const RoleIcon = meta.icon;
  const roleText = member.isGestor
    ? t("teamPage.role.labels.gestor", { defaultValue: "Gestor" })
    : roleLabel(member.role);
  const payShort =
    member.pay_type === "daily"
      ? t("teamPage.payType.dailyShort", { defaultValue: "/dia" })
      : t("teamPage.payType.hourlyShort", { defaultValue: "/hora" });

  return (
    <TableRow
      className={cn("group cursor-pointer", !member.is_active && "opacity-60")}
      onClick={onEdit}
    >
      <TableCell>
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
              meta.avatar,
            )}
            aria-hidden
          >
            {memberInitials(member)}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">
                {member.full_name?.trim() || member.email}
              </span>
              {isMe && (
                <Badge variant="outline" className="text-[10px]">
                  {t("teamPage.table.you")}
                </Badge>
              )}
              <CompanyBadge companyId={member.company_id} />
            </div>
            <div className="truncate text-xs text-muted-foreground">{member.email}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {member.phone ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Phone className="h-3.5 w-3.5 shrink-0" />
            {member.phone}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        {member.role ? (
          <Badge variant="outline" className={cn("gap-1 rounded-full font-normal", meta.badge)}>
            <RoleIcon className="h-3 w-3" />
            {roleText}
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <span className="whitespace-nowrap text-sm">
          <span className="font-medium">{member.hourly_rate}</span>
          <span className="text-muted-foreground">
            {payShort} · {currency}
          </span>
        </span>
      </TableCell>
      <TableCell>
        <IntakeStatusBadge status={member.intakeStatus} />
      </TableCell>
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={member.is_active}
          disabled={isMe}
          onCheckedChange={(v) => onActiveToggle(member, v)}
        />
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100"
            onClick={onEdit}
            title={t("teamPage.edit.open", { defaultValue: "Editar membro" })}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onShowQr}>
                <QrCode className="mr-2 h-4 w-4" />
                {t("teamPage.actions.showQr")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onShareWhatsApp}>
                <MessageCircle className="mr-2 h-4 w-4" />
                {t("teamPage.actions.sendClockLink")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenWhatsAppChat}>
                <MessageCircle className="mr-2 h-4 w-4" />
                {t("teamPage.actions.openWhatsApp", { defaultValue: "Abrir conversa no WhatsApp" })}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onCopyLink}>
                <Link2 className="mr-2 h-4 w-4" />
                {t("teamPage.actions.copyClockLink")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onRotateLink}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t("teamPage.actions.rotateLink")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onResetPassword}>
                <KeyRound className="mr-2 h-4 w-4" />
                {t("teamPage.actions.resetPassword")}
              </DropdownMenuItem>
              {canGrantCompany && (
                <DropdownMenuItem onClick={onGrantCompany}>
                  <Building2 className="mr-2 h-4 w-4" />
                  {t("teamPage.actions.grantCompany", {
                    defaultValue: "Dar acesso a outra empresa",
                  })}
                </DropdownMenuItem>
              )}
              {!isMe && (
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("teamPage.actions.deleteEmployee", { defaultValue: "Excluir funcionário" })}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}

type MemberFns = {
  updateName: (userId: string, fullName: string) => Promise<unknown>;
  updateEmail: (userId: string, email: string) => Promise<unknown>;
  updatePhone: (userId: string, phone: string | null) => Promise<unknown>;
  updateRole: (userId: string, role: AppRole) => Promise<unknown>;
  upsertRate: (userId: string, hourlyRate: number, payType: PayType) => Promise<unknown>;
  updateNotes: (userId: string, notes: string | null) => Promise<unknown>;
};

function MemberEditSheet({
  member,
  isMe,
  currency,
  onClose,
  onSaved,
  fns,
}: {
  member: Member | null;
  isMe: boolean;
  currency: string;
  onClose: () => void;
  onSaved: () => void;
  fns: MemberFns;
}) {
  const { t } = useTranslation();
  const roleLabel = useRoleLabel();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<AppRole | "">("");
  const [rate, setRate] = useState("0");
  const [payType, setPayType] = useState<PayType>("hourly");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!member) return;
    setName(member.full_name ?? "");
    setEmail(member.email ?? "");
    setPhone(member.phone ?? "");
    setRole(member.role ?? "");
    setRate(member.hourly_rate.toString());
    setPayType(member.pay_type);
    setNotes(member.notes ?? "");
  }, [member]);

  const handleSave = async () => {
    if (!member) return;
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const parsedRate = Number(rate.replace(",", "."));

    // Phone: validate/normalize only when it changed and is present.
    let normalizedPhone: string | null = member.phone ?? null;
    if (trimmedPhone !== (member.phone ?? "")) {
      if (trimmedPhone) {
        const result = validateAndNormalizePhone(trimmedPhone);
        if (!result.ok) {
          toast.error(t("teamPage.phone.invalid"), { description: result.error });
          return;
        }
        normalizedPhone = result.e164;
      } else {
        normalizedPhone = null;
      }
    }

    setSaving(true);
    try {
      if (trimmedName && trimmedName !== (member.full_name ?? "")) {
        await fns.updateName(member.id, trimmedName);
      }
      if (trimmedEmail && trimmedEmail.toLowerCase() !== (member.email ?? "").toLowerCase()) {
        await fns.updateEmail(member.id, trimmedEmail);
      }
      if ((normalizedPhone ?? null) !== (member.phone ?? null)) {
        await fns.updatePhone(member.id, normalizedPhone);
      }
      if (!isMe && role && role !== member.role) {
        await fns.updateRole(member.id, role);
      }
      if (
        !Number.isNaN(parsedRate) &&
        parsedRate >= 0 &&
        (parsedRate !== member.hourly_rate || payType !== member.pay_type)
      ) {
        await fns.upsertRate(member.id, parsedRate, payType);
      }
      if ((notes.trim() || null) !== (member.notes ?? null)) {
        await fns.updateNotes(member.id, notes.trim() || null);
      }
      toast.success(t("teamPage.edit.saved", { defaultValue: "Alterações salvas" }));
      onSaved();
    } catch (e) {
      toast.error(t("teamPage.edit.saveFailed", { defaultValue: "Falha ao salvar" }), {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const meta = member?.isGestor ? GESTOR_META : roleMeta(member?.role ?? null);
  const RoleIcon = meta.icon;

  return (
    <Sheet open={!!member} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-md">
        {member && (
          <>
            <SheetHeader className="space-y-0 text-left">
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    "flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                    meta.avatar,
                  )}
                  aria-hidden
                >
                  {memberInitials(member)}
                </span>
                <div className="min-w-0">
                  <SheetTitle className="truncate">
                    {member.full_name?.trim() || member.email}
                  </SheetTitle>
                  {(member.role || member.isGestor) && (
                    <span
                      className={cn(
                        "mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
                        meta.badge,
                      )}
                    >
                      <RoleIcon className="h-3 w-3" />
                      {member.isGestor
                        ? t("teamPage.role.labels.gestor", { defaultValue: "Gestor" })
                        : roleLabel(member.role)}
                    </span>
                  )}
                </div>
              </div>
              <SheetDescription className="sr-only">
                {t("teamPage.edit.title", { defaultValue: "Editar membro" })}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 flex-1 space-y-4">
              <div className="space-y-2">
                <Label>{t("teamPage.table.name")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{t("teamPage.table.email")}</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{t("teamPage.table.phone")}</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+55 11 98765-4321"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("teamPage.table.role")}</Label>
                <Select
                  value={role || undefined}
                  onValueChange={(v) => setRole(v as AppRole)}
                  disabled={isMe}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_VALUES.map((v) => (
                      <SelectItem key={v} value={v}>
                        {roleLabel(v)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("teamPage.table.hourlyRate")}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="w-28"
                  />
                  <Select value={payType} onValueChange={(v) => setPayType(v as PayType)}>
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">
                        {t("teamPage.payType.hourlyShort", { defaultValue: "/hora" })}
                      </SelectItem>
                      <SelectItem value="daily">
                        {t("teamPage.payType.dailyShort", { defaultValue: "/dia" })}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">{currency}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("teamPage.table.notes")}</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("teamPage.table.notesPlaceholder")}
                  rows={3}
                />
              </div>

              <WorkerIntakeCard userId={member.id} email={member.email} is1099={member.is1099} />
            </div>

            <SheetFooter className="mt-6 flex-row justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                {t("teamPage.addDialog.cancel", { defaultValue: "Cancelar" })}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("teamPage.edit.save", { defaultValue: "Salvar" })}
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function AddMemberDialog({
  onClose,
  onCreated,
  createFn,
}: {
  onClose: () => void;
  onCreated: (res: { email: string; tempPassword: string | null }) => void;
  createFn: ReturnType<typeof useServerFn<typeof createTeamMember>>;
}) {
  const { t } = useTranslation();
  const roleLabel = useRoleLabel();
  const [tab, setTab] = useState<"direct" | "invite">("direct");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [role, setRole] = useState<AppRole>("employee");
  const [hourlyRate, setHourlyRate] = useState("0");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!fullName.trim()) {
      toast.error(t("teamPage.addDialog.errors.nameRequired"));
      return;
    }
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedEmail && !trimmedPhone) {
      toast.error(t("teamPage.addDialog.errors.contactRequired"));
      return;
    }
    if (tab === "direct" && password.length < 8) {
      toast.error(t("teamPage.addDialog.errors.passwordMin"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await createFn({
        data: {
          email: trimmedEmail || null,
          fullName: fullName.trim(),
          phone: trimmedPhone || null,
          notes: notes.trim() || null,
          role,
          hourlyRate: Number(hourlyRate) || 0,
          mode: tab,
          password: tab === "direct" ? password : undefined,
        },
      });
      onCreated({ email: trimmedEmail || trimmedPhone, tempPassword: res.tempPassword });
    } catch (e) {
      toast.error(t("teamPage.addDialog.errors.createFailed"), {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{t("teamPage.addDialog.title")}</DialogTitle>
        <DialogDescription>{t("teamPage.addDialog.description")}</DialogDescription>
      </DialogHeader>
      <Tabs value={tab} onValueChange={(v) => setTab(v as "direct" | "invite")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="direct">{t("teamPage.addDialog.tabs.direct")}</TabsTrigger>
          <TabsTrigger value="invite">{t("teamPage.addDialog.tabs.invite")}</TabsTrigger>
        </TabsList>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>{t("teamPage.addDialog.fullName")}</Label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t("teamPage.addDialog.fullNamePlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("teamPage.addDialog.emailOptional")}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
            />
            <p className="text-xs text-muted-foreground">{t("teamPage.addDialog.emailHint")}</p>
          </div>
          <div className="space-y-2">
            <Label>{t("teamPage.addDialog.phone")}</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+55 11 98765-4321"
            />
            <p className="text-xs text-muted-foreground">{t("teamPage.addDialog.phoneHint")}</p>
          </div>
          <div className="space-y-2">
            <Label>{t("teamPage.addDialog.notes")}</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("teamPage.table.notesPlaceholder")}
            />
            <p className="text-xs text-muted-foreground">{t("teamPage.addDialog.notesHint")}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("teamPage.addDialog.role")}</Label>
              <Select value={role} onValueChange={(v) => setRole(v as AppRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_VALUES.map((v) => (
                    <SelectItem key={v} value={v}>
                      {roleLabel(v)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("teamPage.addDialog.hourlyRate")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
              />
            </div>
          </div>
          <TabsContent value="direct" className="space-y-2 mt-0">
            <Label>{t("teamPage.addDialog.initialPassword")}</Label>
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("teamPage.addDialog.passwordPlaceholder")}
            />
            <p className="text-xs text-muted-foreground">{t("teamPage.addDialog.passwordHint")}</p>
          </TabsContent>
          <TabsContent value="invite" className="mt-0">
            <p className="text-xs text-muted-foreground">{t("teamPage.addDialog.inviteHint")}</p>
          </TabsContent>
        </div>
      </Tabs>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          {t("teamPage.addDialog.cancel")}
        </Button>
        <Button onClick={submit} disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("teamPage.addDialog.submit")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ResetPasswordDialog({
  member,
  onClose,
  onDone,
  resetFn,
}: {
  member: Member;
  onClose: () => void;
  onDone: () => void;
  resetFn: ReturnType<typeof useServerFn<typeof resetMemberPassword>>;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (password.length < 8) {
      toast.error(t("teamPage.resetDialog.passwordMin"));
      return;
    }
    setSubmitting(true);
    try {
      await resetFn({ data: { userId: member.id, password } });
      toast.success(t("teamPage.resetDialog.success"));
      onDone();
    } catch (e) {
      toast.error(t("teamPage.resetDialog.failed"), {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{t("teamPage.resetDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("teamPage.resetDialog.description", { name: member.full_name ?? member.email })}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label>{t("teamPage.resetDialog.newPassword")}</Label>
        <Input
          type="text"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("teamPage.resetDialog.passwordPlaceholder")}
        />
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          {t("teamPage.resetDialog.cancel")}
        </Button>
        <Button onClick={submit} disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("teamPage.resetDialog.submit")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ShowPasswordDialog({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(`Email: ${email}\nPassword: ${password}`);
    setCopied(true);
    toast.success(t("teamPage.showPassword.copied"));
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{t("teamPage.showPassword.title")}</DialogTitle>
        <DialogDescription>{t("teamPage.showPassword.description")}</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 rounded-md border bg-muted/30 p-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("teamPage.showPassword.email")}
          </Label>
          <p className="font-mono text-sm">{email}</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("teamPage.showPassword.tempPassword")}
          </Label>
          <p className="font-mono text-sm">{password}</p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={copy} className="gap-2">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {t("teamPage.showPassword.copy")}
        </Button>
        <Button onClick={onClose}>{t("teamPage.showPassword.done")}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
